import { TextTooltip } from '@components'
import { motion, useReducedMotion } from 'framer-motion'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  FaChevronDown,
  FaCompressArrowsAlt,
  FaGift,
  FaGripVertical,
  FaQuestionCircle,
  FaTimes
} from 'react-icons/fa'
import styled, { css } from 'styled-components'
import { ProgressRing } from './ProgressRing'

interface Props {
  title: string
  handled: number
  total: number
  earned: number
  creditTotal: number
  isCounting: boolean
  hasDragged: boolean
  showSnapBack: boolean
  showIntroHelp: boolean
  introTooltip?: string
  isDragging: boolean
  onPointerDown: (event: React.PointerEvent) => void
  onSnapBack: () => void
  onCollapse: () => void
  onDismiss?: () => void
}

export const CompanionHeader: React.FC<Props> = ({
  title,
  handled,
  total,
  earned,
  creditTotal,
  isCounting,
  hasDragged,
  showSnapBack,
  showIntroHelp,
  introTooltip,
  isDragging,
  onPointerDown,
  onSnapBack,
  onCollapse,
  onDismiss
}) => {
  const [t] = useTranslation()
  const reduceMotion = useReducedMotion()

  return (
    <Header
      onPointerDown={onPointerDown}
      $dragging={isDragging}
      tabIndex={0}
      aria-label={t('dashboard.getStartedWidget.dragHint')}
    >
      <Grip $nudge={!hasDragged && !reduceMotion} aria-hidden={true}>
        <FaGripVertical />
      </Grip>
      <ProgressRing handled={handled} total={total} size="panel" />
      <Copy>
        <Title>{title}</Title>
        {creditTotal > 0 && (
          <Balance
            $bright={isCounting}
            animate={isCounting && !reduceMotion ? { y: [0, -3, 0] } : { y: 0 }}
            transition={{ duration: 0.42, ease: 'easeOut' }}
          >
            <FaGift />
            <strong>
              {earned} / {creditTotal}
            </strong>
            <span>{t('dashboard.getStartedWidget.freeCreditsEarned')}</span>
          </Balance>
        )}
      </Copy>
      <Controls>
        {showIntroHelp && (
          <IntroHelp onPointerDown={event => event.stopPropagation()}>
            <TextTooltip
              tooltip={introTooltip ?? t('dashboard.getStartedWidget.intro')}
              placement="bottom-end"
              maxWidth={300}
            >
              <FaQuestionCircle aria-label={t('dashboard.getStartedWidget.introHelp')} />
            </TextTooltip>
          </IntroHelp>
        )}
        {showSnapBack && (
          <IconButton
            type="button"
            $filled
            onPointerDown={event => event.stopPropagation()}
            onClick={onSnapBack}
            aria-label={t('dashboard.getStartedWidget.snapBack')}
            title={t('dashboard.getStartedWidget.snapBack')}
          >
            <FaCompressArrowsAlt />
          </IconButton>
        )}
        <IconButton
          type="button"
          onPointerDown={event => event.stopPropagation()}
          onClick={onCollapse}
          aria-label={t('dashboard.getStartedWidget.collapse')}
          title={t('dashboard.getStartedWidget.collapse')}
        >
          <FaChevronDown />
        </IconButton>
        {onDismiss && (
          <IconButton
            type="button"
            onPointerDown={event => event.stopPropagation()}
            onClick={onDismiss}
            aria-label={t('general.close')}
            title={t('general.close')}
          >
            <FaTimes />
          </IconButton>
        )}
      </Controls>
    </Header>
  )
}

const Header = styled.div<{ $dragging: boolean }>`
  background: ${({ theme }) => theme.colors.menuBg};
  padding: ${({ theme }) =>
    `${theme.spacing.lg}rem ${theme.spacing.md}rem ${theme.spacing.lg}rem ${theme.spacing.sm}rem`};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  cursor: ${({ $dragging }) => ($dragging ? 'grabbing' : 'grab')};
  touch-action: none;
  user-select: none;

  &:hover {
    background: color-mix(in srgb, ${({ theme }) => theme.colors.menuBg} 88%, white);
  }
`

const Grip = styled.span<{ $nudge: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 0.75rem;
  flex: 0 0 0.75rem;
  color: ${({ $nudge, theme }) =>
    $nudge ? theme.dashboard.onNavyLabel : theme.dashboard.onNavyAxis};
  font-size: ${({ theme }) => theme.fontSize.px14}rem;

  ${({ $nudge }) =>
    $nudge &&
    css`
      animation: companionGripNudge 3.2s ease-in-out infinite;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `}

  @keyframes companionGripNudge {
    0%,
    70%,
    100% {
      transform: translateX(0);
    }
    80% {
      transform: translateX(-0.18rem);
    }
    90% {
      transform: translateX(0.18rem);
    }
  }
`

const Copy = styled.div`
  flex: 1;
  min-width: 0;
`

const Title = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralWhite};
`

const Balance = styled(motion.div)<{ $bright: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ $bright, theme }) =>
    $bright ? theme.dashboard.infoSoft : theme.dashboard.onNavyLabel};

  svg {
    flex: 0 0 auto;
    font-size: 0.7rem;
  }

  strong {
    font-family: 'Roboto Mono', ui-monospace, monospace;
    font-variant-numeric: tabular-nums;
    font-weight: 700;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  flex: 0 0 auto;
`

const IntroHelp = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  flex: 0 0 1.5rem;
  color: ${({ theme }) => theme.dashboard.onNavyLabel};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  cursor: help;

  &:hover {
    color: ${({ theme }) => theme.colors.neutralWhite};
  }
`

const IconButton = styled.button<{ $filled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  flex: 0 0 1.5rem;
  border: none;
  border-radius: ${({ theme }) => theme.spacing.xs}rem;
  background: ${({ $filled }) => ($filled ? 'rgba(255, 255, 255, 0.1)' : 'transparent')};
  color: ${({ theme }) => theme.dashboard.onNavyLabel};
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;

  &:hover {
    background: ${({ $filled }) =>
      $filled ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
  }
`
